import { consume } from '@lit/context';
import { isAxiosError } from 'axios';
import { LitElement, PropertyValueMap } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  ColorOptionDto3,
  ConfiguratorLayoutEnum,
  CustomQuantityOptionDto,
  getGetConfiguratorQuantityStep,
  QuantityStepBaseDto,
  StepEnum,
} from '@/api-sdk';
import {
  ConfiguratorContextType,
  configuratorGlobal,
  global,
  GlobalComponentType,
} from '@/context';
import { QuantityCollapsibleData, QuantityData } from '@/types';
import {
  getParamsObjectFromQueryString,
  isScrolledIntoView,
  optionParamsWithQuantities,
  scrollStepIntoView,
} from '@/utils';

type Constructor<T> = { new (...args: any[]): T };

export const QuantityStepMixin = <T extends Constructor<LitElement>>(
  superClass: T,
) => {
  class QuantityMixinClass extends superClass {
    /**
     * Array of colors {@link ColorOptionDto3} to render the chosen colors
     *
     * @property colors
     */
    @property({ type: Array })
    colors: ColorOptionDto3[] = [];

    /**
     * Custom quantity option to render the custom quantity input
     *
     * @property customQuantityOption
     */
    @property({ type: Object })
    customQuantityOption?: CustomQuantityOptionDto;

    /**
     * Used to render the right quantity step that is needed for the product
     *
     * @property quantityStep
     */
    @property({ type: String })
    quantityStep?: StepEnum;

    /**
     * Query string containing the configuration data, and is used to fetch the quantity data.
     */
    @property({ type: String })
    queryString = '';

    /**
     * Boolean to trigger the scroll to step
     *
     * @property scrollToStep
     */
    @property({ type: Boolean })
    scrollToStep = false;

    /**
     * Object containing all data for the quantity step, which is set by the quantity call.
     *
     */
    @state()
    quantityData?: QuantityData | QuantityCollapsibleData;

    /**
     * Boolean to show the loading state of the quantity step
     */
    @state()
    loading = false;

    /**
     * Promise to contain the quantity data promise.
     */
    @state()
    quantityDataPromise: Promise<QuantityStepBaseDto> | null = null;

    @state()
    controller: AbortController | null = null;

    @state()
    validInputs: { [key: number]: boolean } = {};

    @consume({ context: global.context })
    globalContext!: GlobalComponentType;

    @consume({ context: configuratorGlobal.context, subscribe: true })
    configuratorContext!: ConfiguratorContextType;

    async willUpdate(_changedProperties: PropertyValueMap<any>): Promise<void> {
      const { layout, locale, debtor, country } = this.globalContext;
      const {
        product,
        salable,
        configurationId,
        colorCollapsible,
        quantityCollapsible,
      } = this.configuratorContext;

      if (_changedProperties.has('queryString')) {
        this.loading = true;

        if (this.quantityDataPromise && this.controller) {
          this.controller.abort();
        }

        const paramsFromQueryString = getParamsObjectFromQueryString(
            this.queryString,
          ),
          optionParams = optionParamsWithQuantities(paramsFromQueryString);

        if (!product || (layout === 'wholesale' && !salable)) {
          return;
        }
        try {
          this.controller = new AbortController();
          this.quantityDataPromise = getGetConfiguratorQuantityStep(
            {
              code: product,
              params: {
                configurationId: configurationId || undefined,
                colorCollapsible: colorCollapsible,
                quantityCollapsible: quantityCollapsible,
                locale: locale,
                layout:
                  layout === 'retail'
                    ? ConfiguratorLayoutEnum.retail
                    : ConfiguratorLayoutEnum.wholesale,
                debtor: debtor,
                country: country,
                ...optionParams,
              },
            },
            {
              signal: this.controller?.signal,
            },
          );

          const quantityData: QuantityData = await this.quantityDataPromise;

          if (quantityData) {
            this.quantityData = quantityData;

            this.dispatchEvent(
              new CustomEvent('quantity-validation', {
                composed: true,
                detail: {
                  step: this.quantityStep,
                  validationMessage: quantityData.validationMessage,
                },
              }),
            );
          }

          this.quantityDataPromise = null;
        } catch (error) {
          if (isAxiosError(error) && error.name !== 'CanceledError') {
            console.error(error);
          }
        } finally {
          this.loading = false;
        }
      }

      if (_changedProperties.has('scrollToStep') && this.scrollToStep) {
        scrollStepIntoView(this);
        isScrolledIntoView(this);
      }

      if (_changedProperties.has('validInputs')) {
        if (!this.validInputs) {
          return;
        }

        let valid = false;

        if (Object.values(this.validInputs).includes(false)) {
          valid = false;
        } else {
          valid = true;
        }
        this.dispatchEvent(
          new CustomEvent('validate-variant-stock', {
            composed: true,
            detail: {
              valid: valid,
            },
          }),
        );
      }
    }
  }

  return QuantityMixinClass;
};
